import styles from './CandidatePortal.module.scss';
import Loader from 'components/atoms/Loader/Loader';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import { appSwitchRoutes } from 'constants/routes';
import {
  CandidateJourneyStageStatus,
  StageType,
  useCandidatePortalRoleQuery,
} from 'generated/graphql';
import { FC, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { format as formatDate } from 'date-fns';
import { Navigate, useParams } from 'react-router-dom';
import {
  Icon,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  JourneyTile,
  getCompanyLogoUrl,
  getPortalBrandingStyle,
  selectUpNextTile,
} from './CandidatePortal.helpers';
import {
  deadlineCalendarDay,
  formatDeadlineUtc,
  isPastDeadline,
  parseApiDeadline,
} from 'helpers/deadline';
import JourneyTileCard from './JourneyTileCard/JourneyTileCard';
import PortalDisclaimer from './PortalDisclaimer/PortalDisclaimer';
import NextStepCard from './NextStepCard/NextStepCard';
import PortalFooter from './PortalLayout/PortalFooter';
import PortalNavbar from './PortalLayout/PortalNavbar';

const CandidatePortal: FC = () => {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const { handleMsgType } = useNotification();

  const { data, loading } = useCandidatePortalRoleQuery({
    variables: { id: roleId as string },
    skip: !roleId,
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
    },
    fetchPolicy: 'network-only',
  });

  const role = data?.myRole;

  // Offer stages have no candidate-facing experience yet — hide their tiles
  // (and keep them out of the progress counter) until one exists.
  const tiles = (role?.candidateJourney ?? []).filter(
    tile => tile.stageType !== StageType.OfferStage
  );
  const hasFailedStage = tiles.some(
    tile => tile.status === CandidateJourneyStageStatus.Unsuccessful
  );
  // Hiring process deadline: once it passes, unfinished stages close (the
  // recruiter can still progress the candidate manually; reminders stop).
  const deadlineAt = parseApiDeadline(role?.deadlineAt);
  const hasDeadlinePassed = isPastDeadline(deadlineAt);
  const completedCount = tiles.filter(
    tile => tile.status === CandidateJourneyStageStatus.Completed
  ).length;
  const allComplete = tiles.length > 0 && completedCount === tiles.length;
  const isJourneyClosed = hasFailedStage || (hasDeadlinePassed && !allComplete);

  const getTileLink = (tile: JourneyTile) => {
    if (
      !role ||
      !roleId ||
      isJourneyClosed ||
      tile.status !== CandidateJourneyStageStatus.Unlocked ||
      !tile.stageId
    ) {
      return undefined;
    }
    if (tile.stageType === StageType.ContentStage) {
      return appSwitchRoutes.roleStageContent.getUrl(roleId, tile.stageId);
    }
    if (tile.stageType === StageType.ShortlistStage) {
      // Only shortlist stages carrying eligibility questions have a
      // candidate-facing page; AI-only shortlisting happens recruiter-side.
      const stageId = tile.stageId;
      return role.questionnaireStages.some(stage => stage.id === stageId)
        ? appSwitchRoutes.roleStageEligibility.getUrl(roleId, stageId)
        : undefined;
    }
    return appSwitchRoutes.roleStageActions.getUrl(roleId, tile.stageId);
  };

  // One selection for both the card and the scroll target (PDE-954).
  const upNext = selectUpNextTile(tiles, getTileLink);

  // Bring the "Your next step" card into view once the journey has loaded —
  // the hero and intro can push it below the fold, and the candidate usually
  // lands here straight after finishing a stage.
  const nextStepRef = useRef<HTMLDivElement>(null);
  const hasScrolledToNextStep = useRef(false);
  const upNextStageKey = upNext?.stageId;
  useEffect(() => {
    if (!upNextStageKey || hasScrolledToNextStep.current) {
      return;
    }
    hasScrolledToNextStep.current = true;
    // Let the hero image and cards lay out before measuring.
    const timer = setTimeout(() => {
      nextStepRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [upNextStageKey]);

  if (loading || !role || !roleId) {
    return <Loader isOverlay />;
  }

  if (!role.candidatePortalEnabled) {
    return <Navigate to={appSwitchRoutes.role.getUrl(roleId)} replace />;
  }

  const branding = role.company.branding;
  const logoUrl = getCompanyLogoUrl(role.company);
  const brandingStyle = getPortalBrandingStyle(branding);

  return (
    <div className={styles.portal} style={brandingStyle}>
      <HelmetAndPageAnnouncer pageTitle={role.name} />

      <PortalNavbar companyName={role.company.name} logoUrl={logoUrl} />

      {role.coverPhotoUrl && (
        <div className={styles.hero}>
          <img src={role.coverPhotoUrl} alt="" />
        </div>
      )}

      <main className={styles.main}>
        <h1 className={styles.title}>{role.name}</h1>
        {hasFailedStage ? (
          // A failed stage dead-ends the journey — the disclaimer replaces
          // the branded welcome entirely.
          <PortalDisclaimer
            variant="error"
            icon={<Icon icon="cancel_filled" />}
            label={t('candidatePortal.unsuccessfulBanner.label')}
            title={t('candidatePortal.unsuccessfulBanner.title')}
            body={t('candidatePortal.unsuccessfulBanner.body')}
          />
        ) : hasDeadlinePassed && !allComplete ? (
          <PortalDisclaimer
            variant="neutral"
            icon={<Icon icon="cancel_filled" />}
            label={t('candidatePortal.deadlinePassedBanner.label')}
            title={t('candidatePortal.deadlinePassedBanner.title')}
            body={t('candidatePortal.deadlinePassedBanner.body', {
              deadline: formatDate(
                deadlineCalendarDay(deadlineAt as Date),
                'd MMMM yyyy'
              ),
            })}
          />
        ) : allComplete ? (
          // Every stage is done — the branded welcome makes way for the
          // "application under review" message.
          <PortalIntro
            subtitle={t('candidatePortal.allComplete.title')}
            intro={t('candidatePortal.allComplete.body')}
          />
        ) : (
          <PortalIntro
            subtitle={role.candidatePortalTitle}
            intro={role.candidatePortalIntro}
          />
        )}

        {upNext && !isJourneyClosed && (
          <div ref={nextStepRef} className={styles.nextStepAnchor}>
            <NextStepCard
              tile={upNext}
              stepNumber={tiles.indexOf(upNext) + 1}
              totalSteps={tiles.length}
              link={getTileLink(upNext)}
            />
          </div>
        )}

        <section aria-label={t('candidatePortal.journeyTitle')}>
          <div className={styles.journeyHeader}>
            <div>
              <h2 className={styles.journeyTitle}>
                {t('candidatePortal.journeyTitle')}
              </h2>
              {deadlineAt && (
                <p className={styles.journeyDeadline}>
                  <Icon icon="clock" />
                  <span>
                    <Trans
                      i18nKey={
                        hasDeadlinePassed
                          ? 'candidatePortal.journeyDeadlinePassed'
                          : 'candidatePortal.journeyDeadline'
                      }
                      values={{ deadline: formatDeadlineUtc(deadlineAt) }}
                      components={{ 1: <strong /> }}
                    />
                  </span>
                </p>
              )}
            </div>
            <div className={styles.journeyProgress}>
              <span>
                {t('candidatePortal.progress', {
                  completed: completedCount,
                  total: tiles.length,
                })}
              </span>
              <span className={styles.progressBar}>
                <span
                  className={styles.progressBarFill}
                  style={{
                    width: `${(completedCount / (tiles.length || 1)) * 100}%`,
                  }}
                />
              </span>
            </div>
          </div>

          <div className={styles.journeyGrid}>
            {tiles.map((tile, index) => (
              <JourneyTileCard
                key={tile.stageId ?? `${tile.stageType}-${index}`}
                tile={tile}
                link={getTileLink(tile)}
              />
            ))}
          </div>
        </section>
      </main>

      <PortalFooter
        companyName={role.company.name}
        websiteLinkUrl={branding?.websiteLinkUrl}
        careersLinkUrl={branding?.careersLinkUrl}
      />
    </div>
  );
};

// The branded welcome under the role name. Callers pick the values — the
// role's own portal title/intro, or the all-complete review message.
function PortalIntro({
  subtitle,
  intro,
}: {
  subtitle?: string | null;
  intro?: string | null;
}) {
  return (
    <>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      {intro && <p className={styles.intro}>{intro}</p>}
    </>
  );
}

export default CandidatePortal;
